import React from 'react';
import { Box } from 'rebass/styled-components';

import { layout } from '../../constants/constants';
import ContentAccordion from '../../shared/components/accordion/content-accordion';
import CallToAction from '../../shared/components/call-to-actions/call-to-action/call-to-action';
import CardSlider from '../../shared/components/card-slider/card-slider';
import ContentCardGrid from '../../shared/components/content-card-grid/content-card-grid';
import LocationCardGrid from '../../shared/components/location-card-grid/location-card-grid';
import NonClickableContentCardGrid from '../../shared/components/non-clickable-content-card-grid/non-clickable-content-card-grid';
import ProviderCardGrid from '../../shared/components/provider-card-grid/provider-card-grid';
import SingleColumnBlock from '../../shared/components/single-column-block/single-column-block';
import SocialMediaButtons from '../../shared/components/social-media-buttons/social-media-buttons';
import Table from '../../shared/components/table/table';
import TestimonialSlider from '../../shared/components/testimonial-slider/testimonial-slider';
import TwoColumnBlock from '../../shared/components/two-column-block/two-column-block';
import { FeatureToggle, IconPack, PageBody } from '../../shared/models/app-data-model';
import './page-modular-block.scss';

export type PageModularBlockProps = {
  blockContent: PageBody;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
  heroType?: string;
  campaignPageActive?: boolean;
  corewellStyleOverrides?: boolean;
};

const PageModularBlock: React.FC<PageModularBlockProps> = ({
  blockContent,
  featureToggles,
  iconPack,
  heroType,
  campaignPageActive,
  corewellStyleOverrides
}: PageModularBlockProps) => {
  // this var accounts for naming convention differences in UIDs between chorg and shorg for the nonClickable content card block
  let nonClickableCardGrid = blockContent.Non_Clickable_Content_Card;
  if (blockContent.non_clickable_content_card) {
    nonClickableCardGrid = blockContent.non_clickable_content_card;
  }

  const rendering = (
    <>
      {blockContent.single_column_content_block && (
        <SingleColumnBlock
          column={blockContent.single_column_content_block}
          heroType={heroType}
          iconPack={iconPack}
          featureToggles={featureToggles}
          campaignPageActive={campaignPageActive}
        />
      )}
      {blockContent.two_column_content_block && (
        <TwoColumnBlock
          content={blockContent.two_column_content_block}
          featureToggles={featureToggles}
          iconPack={iconPack}
          campaignPageActive={campaignPageActive}
        />
      )}
      {blockContent.low_impact_cta_block && <CallToAction iconPack={iconPack} content={blockContent.low_impact_cta_block} impact="low" />}
      {blockContent.high_impact_cta_block && (
        <CallToAction iconPack={iconPack} content={blockContent.high_impact_cta_block} impact="high" />
      )}

      {blockContent.content_card_grid && (
        <ContentCardGrid iconPack={iconPack} content={blockContent.content_card_grid} featureToggles={featureToggles} corewellStyleOverrides={corewellStyleOverrides} />
      )}

      {nonClickableCardGrid && <NonClickableContentCardGrid content={nonClickableCardGrid} featureToggles={featureToggles} corewellStyleOverrides={corewellStyleOverrides} />}

      {blockContent.provider_card_grid && (
        <ProviderCardGrid iconPack={iconPack} content={blockContent.provider_card_grid} featureToggles={featureToggles} />
      )}
      {blockContent.location_card_grid && (
        <LocationCardGrid iconPack={iconPack} content={blockContent.location_card_grid} featureToggles={featureToggles} />
      )}
      {blockContent.accordion && (
        <ContentAccordion contentAccordion={blockContent.accordion} featureToggles={featureToggles} iconPack={iconPack} corewellStyleOverrides={corewellStyleOverrides} />
      )}
      {blockContent.card_slider && <CardSlider content={blockContent.card_slider} featureToggles={featureToggles} iconPack={iconPack} corewellStyleOverrides={corewellStyleOverrides} />}
      {blockContent.testimonial_block && (
        <TestimonialSlider testimonials={blockContent.testimonial_block.testimonials} featureToggles={featureToggles} iconPack={iconPack} />
      )}
      {blockContent.table && <Table content={blockContent.table} featureToggles={featureToggles} />}
      {campaignPageActive && blockContent.social_media && blockContent.social_media.reference.length > 0 && (
        <Box marginX={'auto'} width={layout.pageWidth} paddingY={24}>
          <SocialMediaButtons
            socials={blockContent.social_media.reference[0].social_media_links}
            horizontalAlignment={blockContent.social_media.horizontal_alignment}
          />
        </Box>
      )}
    </>
  );

  return (
    <div
      className={`page-modular-block ${
        campaignPageActive && (blockContent.single_column_content_block || blockContent.two_column_content_block)
          ? 'campaign-no-margin'
          : ''
      }`}
    >
      {rendering}
    </div>
  );
};

export default PageModularBlock;
